import { Button } from "@/components/ui/button";
import { Download, Headset, Calendar, Truck, Anchor } from "lucide-react";
import "flag-icons/css/flag-icons.min.css";

// Basic mapper for demo/common countries. In a real app, use a comprehensive library.
const getCountryCode = (countryName: string) => {
  const map: Record<string, string> = {
    'cameroon': 'cm',
    'france': 'fr',
    'greece': 'gr',
    'netherlands': 'nl',
    'portugal': 'pt',
    'kenya': 'ke',
    'denmark': 'dk',
    'thailand': 'th',
    'malaysia': 'my',
    'united states': 'us',
    'usa': 'us',
    'saudi arabia': 'sa',
    'germany': 'de',
    'spain': 'es'
  };
  const name = countryName?.toLowerCase()?.trim();
  return map[name] || 'un'; // 'un' for unknown/default
};

interface ShipmentOverviewProps {
  shipment: any;
}

export const ShipmentOverview = ({ shipment }: ShipmentOverviewProps) => {
  const originCode = getCountryCode(shipment.origin?.split(',').pop()?.trim() || '');
  const destCode = getCountryCode(shipment.destination?.split(',').pop()?.trim() || '');

  // Extract country names for display
  const originCountry = shipment.origin?.split(',').pop()?.trim();
  const destCountry = shipment.destination?.split(',').pop()?.trim();
  
  // Extract city names for display
  const originCity = shipment.origin?.split(',')[0]?.trim();
  const destCity = shipment.destination?.split(',')[0]?.trim();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
      <h2 className="text-xs font-bold text-slate-800 tracking-wide mb-4 uppercase">Shipment Overview</h2>
      
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 border-b border-gray-100 pb-6 mb-6">
        <div className="flex flex-wrap items-center gap-8">
          {/* Status */}
          <div className="flex items-center gap-2 bg-gray-50/50 p-2.5 rounded-lg border border-gray-100">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              shipment.current_status === 'In Transit' 
                ? 'bg-green-500' 
                : shipment.current_status === 'On Hold'
                  ? 'bg-amber-500'
                  : 'bg-blue-600'
            } text-white shadow-sm`}>
              <Truck size={20} />
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-0.5">Status</p>
              <p className={`font-bold ${
                shipment.current_status === 'In Transit' 
                  ? 'text-green-600' 
                  : shipment.current_status === 'On Hold'
                    ? 'text-amber-600'
                    : 'text-blue-700'
              } text-sm`}>
                {shipment.current_status}
              </p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Tracking No.</p>
            <p className="font-bold text-gray-900 text-sm">{shipment.tracking_number}</p>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Consignee</p>
            <p className="font-bold text-gray-900 text-sm">{shipment.receiver_name || 'N/A'}</p>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Service Type</p>
            <p className="font-bold text-gray-900 text-sm">{shipment.service_type}</p>
          </div>

          <div className="space-y-1">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Estimated Arrival</p>
            <div className="flex items-center gap-2 font-bold text-gray-900 text-sm">
              {shipment.estimated_delivery ? new Date(shipment.estimated_delivery).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD'}
              <Calendar size={14} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <Button variant="outline" className="text-blue-700 border-blue-200 hover:bg-blue-50 font-semibold h-9 px-4 text-xs">
            <Headset className="mr-2 h-4 w-4" /> Contact Support
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between px-2">
        {/* Origin */}
        <div className="w-1/3">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Origin</p>
          <div className="flex items-center gap-2">
            <span className={`fi fi-${originCode} text-lg shadow-sm rounded-sm`}></span>
            <p className="font-bold text-gray-900 text-sm truncate max-w-[180px]">{originCity || originCountry || 'N/A'}</p>
          </div>
        </div>

        {/* Path graphic */}
        <div className="flex-1 flex items-center justify-center px-4 relative">
          <div className="w-full border-t-2 border-dashed border-gray-300 relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-blue-600">
              <Anchor size={16} />
            </div>
          </div>
        </div>

        {/* Destination */}
        <div className="w-1/3 text-right flex flex-col items-end">
          <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mb-1">Destination</p>
          <div className="flex items-center gap-2 justify-end">
            <p className="font-bold text-gray-900 text-sm truncate max-w-[180px]">{destCity || destCountry || 'N/A'}</p>
            <span className={`fi fi-${destCode} text-lg shadow-sm rounded-sm`}></span>
          </div>
        </div>
      </div>
    </div>
  );
};
